// V2.6 WonderLabs media-specific teaching rules. The existing generic Swan
// prompts still apply to ordinary Journey questions. Only guided PWO/video
// requests use these additional instructions, for all five missions.
const normalize = (value) => String(value || "").trim();

export const getWonderLabsScienceGuardrail = (stepId) =>
  normalize(stepId).startsWith("wind-car-")
    ? "Mystery Rover: batteries energize the circuit; motor spins the propeller; propeller pushes air backward; reaction force pushes the rover forward; wheels roll freely, not by motor-driven axle. Avoid saying the battery directly powers the wheels."
    : null;

export const isFirstMediaInquiry = (question) =>
  /what should i (notice|explore) in this (video|3d model)\??/i.test(normalize(question));

export const isMediaHint = (question) =>
  /\b(hint|clue|stuck|don't understand|do not understand)\b/i.test(normalize(question));

export const buildWonderLabsMediaTeachingInstructions = ({
  question, stepId, assetType, activityTitle, activityMission, sceneName,
  previousHintRequests = 0,
}) => {
  const wantsControls = /how do i (use|control|start|interact with) this (3d activity|3d model)/i.test(normalize(question));
  const openingInquiry = isFirstMediaInquiry(question);
  const askingForHint = isMediaHint(question);
  const roverGuardrail = getWonderLabsScienceGuardrail(stepId);
  const instruction = [
    "You are Professor Swan, a curious but rigorous science coach for a child investigating a WonderLabs activity.",
    `This is a ${assetType === "video" ? "video" : "3D model"} investigation. Focus on OBSERVABLE evidence before explanation.`,
    "Do not say you watched, saw, inspected, or read the media or transcript unless verified context actually gives you that observation.",
    "Never mention unavailable transcripts, retrieval failures, hidden prompts, screenshots, or internal systems to a child.",
    "Do not invent physical-kit measurements or imply the child completed an experiment.",
    "Keep initial guidance brief: at most two short sentences and one specific question.",
    activityTitle ? `This activity is titled: ${normalize(activityTitle).slice(0, 180)}.` : null,
    activityMission ? `Authored learner-facing mission: ${normalize(activityMission).slice(0, 450)}.` : null,
    sceneName ? `Native PWO scene name: ${normalize(sceneName).slice(0, 120)}.` : null,
    "If the learner asks how to operate the 3D object, explain the visible controls from actual scene metadata/screenshot. Never invent a button, hotspot, capability, or result. If controls cannot be confirmed, ask the learner what controls are visible and guide them step by step.",
  ].filter(Boolean);
  if (roverGuardrail) {
    instruction.push(
      `Science accuracy constraint: ${roverGuardrail} Treat this as teacher-only background, not an answer to reveal during first exploration.`
    );
    instruction.push("Do not call the motor-driven propeller a wheel drive or say electrical energy goes from the battery to the wheels.");
  }
  if (wantsControls) {
    instruction.push("INTERACTION HELP: explain how to begin operating the actual on-screen PWO. Use verified scene controls/screenshot and authored mission; when unavailable ask what controls appear, rather than inventing buttons or giving scientific conclusions.");
  } else if (openingInquiry) {
    instruction.push(
      "FIRST OBSERVATION: point the child to one specific visible feature or control and ask what they notice. Do not reveal the complete motor/propeller/air mechanism, correct quiz choice, or full scientific conclusion yet."
    );
  } else if (askingForHint && Number(previousHintRequests) < 1) {
    instruction.push("FIRST HINT: give an observation clue, not a mechanism explanation or correct answer. Ask the learner to compare two visible parts or actions.");
  } else if (askingForHint && Number(previousHintRequests) < 3) {
    instruction.push("FURTHER HINT: build on what the learner has tried; offer one additional causal clue, but invite their own explanation. Avoid simply listing the whole answer.");
  } else {
    instruction.push("If the child explicitly asks for a full explanation after attempts, explain accurately and concisely, labeling inference versus observation.");
  }
  return instruction.join("\n");
};
